<template>
    <Teleport to="body">
        <div class="guardian-selection-modal-wrapper">
            <Modal :visible="visible" @close="closeModal">
                <div class="modal-header-section">
                    <h2 class="modal-header-title">
                        {{ $t('guardians.selectionModal.title') }}
                    </h2>
                </div>

                <div class="modal-body-section">
                    <p class="text-muted">
                        {{ $t('guardians.selectionModal.description') }}
                    </p>
                    <div class="guardians-grid">
                        <div
                            v-for="guardian in filteredGuardians"
                            :key="guardian.id"
                            class="guardian-card"
                            :class="{
                                selected: selectedGuardian?.id === guardian.id,
                            }"
                            @click="selectGuardian(guardian)"
                        >
                            <DotLottieVue
                                v-if="isLottieAnimation(guardian.image)"
                                :data="guardian.image"
                                class="guardian-image"
                                loop
                                autoplay
                            />
                            <img
                                v-else-if="guardian.image"
                                :src="getImageSrc(guardian.image)"
                                :alt="guardian.name"
                                class="guardian-image"
                            />
                            <div v-else class="guardian-placeholder">
                                <i class="fi fi-robot"></i>
                            </div>

                            <div class="guardian-label">
                                {{ guardianLabel(guardian) }}
                            </div>
                            <p class="guardian-name text-muted">
                                {{ guardian.name }}
                            </p>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    </Teleport>
</template>

<script>
import Modal from '@/components/Modal.vue';
import { DotLottieVue } from '@lottiefiles/dotlottie-vue';
import { useGuardianStore } from '@/stores/guardian';
import { usePattyStore } from '@/stores/patty';
import { GuardianFilterMixin } from '@/mixins/GuardianFilterMixin.js';
import { GUARDIANS } from '@/config/guardians.js';
import { getGuardianLabel } from '@/components/guardians/utils/guardianDisplay';

export default {
    name: 'GuardianSelectionModal',
    components: {
        Modal,
        DotLottieVue,
    },
    mixins: [GuardianFilterMixin],
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['close'],
    data() {
        return {
            guardianStore: null,
            pattyStore: null,
            selectedGuardian: null,
            guardians: GUARDIANS,
        };
    },
    async mounted() {
        this.guardianStore = useGuardianStore();
        this.pattyStore = usePattyStore();
        await this.fetchAvailableAgents();
    },
    methods: {
        isLottieAnimation(image) {
            return (
                image &&
                typeof image === 'object' &&
                image.constructor === Object &&
                !image.default
            );
        },
        getImageSrc(image) {
            if (typeof image === 'string') {
                return image;
            }
            if (image && typeof image === 'object') {
                return image.default || image.src || '';
            }
            return '';
        },
        guardianLabel(guardian) {
            return getGuardianLabel(
                guardian,
                this.$t,
                this.$userStore?.company?.country,
                this.$userStore?.effectiveLocale
            );
        },
        selectGuardian(guardian) {
            if (!guardian) return;
            this.selectedGuardian = guardian;
            this.closeModal();

            const wasGuardianOpen = this.guardianStore?.isPanelVisible;

            // Close whichever panel is currently open
            this.pattyStore?.closePanel();
            this.guardianStore?.closePanel();

            // If a panel was already open, wait for its close animation before
            // opening the new one so the transition feels clean
            const delay = wasGuardianOpen ? 300 : 0;
            setTimeout(() => {
                this.guardianStore?.openNew(guardian);
            }, delay);
        },
        closeModal() {
            this.$emit('close');
        },
    },
};
</script>

<style>
/* Override modal width - unscoped to ensure it applies */
.guardian-selection-modal-wrapper .custom-modal {
    width: 100%;
    max-width: min(36rem, calc(100% - 2 * var(--space-2)));
    background-color: var(--primary-25);
    padding: 0;
    z-index: calc(var(--z-index-modal) + 2);
}

.guardian-selection-modal-wrapper .overlay {
    z-index: calc(var(--z-index-modal) + 1);
}
@media (min-width: 992px) {
    .guardian-selection-modal-wrapper .custom-modal {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
        right: 16.875rem;
        left: auto;
    }
}
</style>

<style scoped>
.modal-header-section {
    background-color: var(--neutral-600);
    padding: 0.875rem var(--space-6);
    border-radius: var(--radius-default) var(--radius-default) 0 0;
}

.modal-header-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--black);
}

.modal-header-subtitle {
    font-size: 0.9375rem;
    color: var(--neutral-800);
    margin: 0;
}

.modal-body-section {
    padding: var(--space-4) var(--space-6);

    p {
        font-size: 0.875rem;
    }
}

.guardians-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    grid-template-rows: auto;
    gap: var(--space-2);
}

.guardian-card {
    display: flex;
    padding: var(--space-2);
    flex-direction: column;
    align-items: center;
    gap: var(--space-1);
    border-radius: var(--radius-default);
    background: var(--neutral-400);
    border: 2px solid transparent;
    cursor: pointer;
    transition: border 0.25s ease, box-shadow 0.25s ease;
    box-sizing: border-box;
}

.guardian-card:hover {
    border-radius: var(--radius-default);
    border: 2px solid var(--neutral-600);
    box-shadow: 0 0 8px 0 var(--primary-200);
}

.guardian-image {
    width: 80px;
    height: 90px;
    object-fit: contain;
    flex-shrink: 0;
    background-color: transparent;
    border-radius: var(--radius-default);
}

.guardian-placeholder {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: var(--neutral-400);
    background-color: var(--white);
    border-radius: var(--radius-default);
}

.guardian-label {
    color: var(--black, #2d2d2d);
    font-family: 'Montserrat', sans-serif;
    font-size: 1rem;
    font-style: normal;
    font-weight: 600;
    text-align: center;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.guardian-name {
    font-size: 0.75rem;
    color: var(--neutral-600);
    text-align: center;
    margin: 0;
}

@media (max-width: 767.98px) {
    .modal-header-section {
        padding-inline: var(--space-4);
    }

    .modal-body-section {
        padding: var(--space-4);
    }

    .guardians-grid {
        gap: var(--space-3);
    }

    .guardian-image {
        width: 60px;
        height: 60px;
    }

    .guardian-label {
        font-size: 0.875rem;
    }

    .guardian-placeholder {
        width: 60px;
        height: 60px;
        font-size: 1.5rem;
    }
}
</style>
